import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { Layout } from "@/components/Layout";
import { ratesQueryOptions } from "@/components/CountryPage";
import { STATIC_ALIASES, buildAliasHead } from "@/lib/alias-routes";
import { buildCalculatorLd } from "@/lib/page-schemas";

const A = STATIC_ALIASES.find((x) => x.slug === "gold-purity-calculator")!;
const PurityConverter = lazy(() =>
  import("@/components/GoldToolsSuite").then((m) => ({ default: m.PurityConverter })),
);

export const Route = createFileRoute("/gold-purity-calculator")({
  head: () => {
    const base = buildAliasHead({ title: A.title, description: A.description, path: `/${A.slug}` });
    const url = `https://drhint.com/${A.slug}`;
    return {
      ...base,
      scripts: [
        ...(base.scripts ?? []),
        ...buildCalculatorLd({
          url,
          name: A.title,
          description: A.description,
          featureList: [
            "Convert any weight across 24K / 22K / 21K / 18K / 14K",
            "Live PKR and INR pricing at each purity level",
            "Per-gram, per-tola, per-10-gram and per-ounce outputs",
            "Side-by-side karat comparison table",
          ],
          howTo: {
            name: "How to convert gold weight across karats",
            totalTime: "PT1M",
            steps: [
              { name: "Enter source weight and karat", text: "Type the gold weight you have (in grams, tola or ounce) and select the karat it is — for example 22K." },
              { name: "Pick the target karat", text: "Choose the karat you want to convert to (e.g. 24K equivalent). The calculator applies the purity ratio: 22K → 24K multiplies weight by 0.9167 / 1.0000 of pure gold content." },
              { name: "Read the equivalent weight and live value", text: "You get the equivalent pure-gold weight plus today's live PKR and INR value at the current 24K spot rate." },
              { name: "Cross-check against the side-by-side table", text: "Below the result, every common karat is listed with its weight and price, so you can sanity-check a jeweller's quote in seconds." },
            ],
          },
          faqs: [
            { q: "What does 22K, 21K, 18K and 14K actually mean?", a: "Karat measures parts of pure gold out of 24. 24K = 99.9% pure, 22K = 91.67% (22/24), 21K = 87.5%, 18K = 75% and 14K = 58.33%. The remainder is alloy (copper, silver, zinc) for hardness or colour." },
            { q: "Why does my 22K jewellery cost more per gram than spot?", a: "Spot price is for 24K pure bullion. 22K jewellery adds making charges (8–18%), wastage (often 1–3%) and the jeweller's margin on top of the purity-adjusted spot. The calculator shows the pure metal value so you can spot how much markup the jeweller is charging." },
            { q: "Is the converter accurate for hallmarked Pakistan / India gold?", a: "Yes. BIS (India) and PSQCA (Pakistan) hallmark standards align with these exact purity ratios. If a jeweller claims a different fineness for the same karat, the gold is either uncertified or being misrepresented." },
          ],
          trail: [
            { name: "Home", item: "https://drhint.com/" },
            { name: "Tools", item: "https://drhint.com/tools" },
            { name: "Gold Purity Calculator", item: url },
          ],
        }),
      ],
    };
  },
  loader: ({ context }) => { void context.queryClient.prefetchQuery(ratesQueryOptions); },
  component: () => (
    <Layout>
      <section className="mx-auto max-w-6xl px-4 pt-10 pb-2">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/tools" className="hover:text-gold">Tools</Link> / Gold Purity Calculator
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{A.title}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">{A.description}</p>
      </section>
      <Suspense fallback={<div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>}>
        <PurityConverter />
      </Suspense>
    </Layout>
  ),
});